import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api, { ensureCsrfToken } from '../utils/api';
import { trackEvent } from '../utils/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, try to restore session from the HttpOnly cookie.
  // api.get('/auth/me') works because withCredentials: true sends the cookie automatically.
  // If the cookie is absent or expired, the 401 response clears user state.
  const fetchUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      return data.user;
    } catch (error) {
      if (error.response?.status === 401) setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prime the CSRF double-submit cookie before the user can submit a login POST,
    // so the first attempt doesn't race the lazy bootstrap (the cause of the
    // intermittent "couldn't verify your session security token" on first login).
    ensureCsrfToken();
    fetchUser();
  }, [fetchUser]);

  // Listen for 401s from the api.js interceptor (e.g. expired token on a
  // protected call while already logged in) and clear auth state so React
  // Router's PrivateRoute can redirect to login without a hard page reload.
  useEffect(() => {
    const handleLogout = () => setUser(null);
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Re-validate the session when a page is restored from the browser's
  // back/forward cache (bfcache). OAuth and other flows navigate away with a
  // full page load (window.location.href), so pressing Back can restore a
  // *stale* pre-login snapshot of this app where `user` is still null — making
  // an authenticated user look logged out (PublicRoute shows the login form)
  // even though their auth cookie is perfectly valid. On a persisted restore we
  // re-fetch from the cookie so the UI reflects the real session.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) fetchUser();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [fetchUser]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user);
    trackEvent('login', { method: 'email' });
    return data.user;
  };

  const register = async (name, email, password, accountType = 'individual', organizationName = '', termsAccepted = true) => {
    const { data } = await api.post('/auth/register', {
      name, email, password, accountType, termsAccepted,
      ...(accountType === 'service_provider' && organizationName ? { organizationName } : {})
    });
    if (data.user) {
      setUser(data.user);
      trackEvent('sign_up', { method: 'email' });
    }
    return data;
  };

  const startGuestSession = useCallback(async (termsAccepted = false) => {
    const { data } = await api.post('/auth/guest', { termsAccepted });
    setUser(data.user);
    return data.user;
  }, []);

  const tryDemo = async () => {
    const { data } = await api.post('/auth/demo');
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* offline / already invalid — fine */ }
    setUser(null);
    // Explicit user-initiated sign-out — confirm it worked (reassuring on a
    // shared device). Not fired for the silent auth:logout expiry handler.
    toast.success("You've been signed out.");
  };

  const updateUser = (updatedUser) => {
    setUser(prev => ({ ...prev, ...updatedUser }));
  };

  const refreshUser = fetchUser;

  // Hydrate auth state directly from a response that already returned `user`
  // (e.g. exchange-code), so the caller can navigate in-SPA without a hard
  // reload that would re-trigger fetchUser() and risk a cookie-timing race.
  const setAuthenticatedUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      startGuestSession,
      tryDemo,
      logout,
      updateUser,
      refreshUser,
      setAuthenticatedUser,
      isAuthenticated: !!user,
      isGuest: !!user?.isGuest
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
